import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDroppable } from "@dnd-kit/core";
import { colors } from "utils";

const ChevronDownIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 9L12 15L18 9" />
  </svg>
);

// Dashed tile that adds an unassigned code to this bundle. Acts both as a click
// menu (pick a code) and as a drop target (drop a code → assign to primary standard).
export const AddGroupCodeTile = ({ assignmentId, unassignedCodes, onAddCode }) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const ref = useRef(null);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const itemRefs = useRef([]);
  const { setNodeRef, isOver } = useDroppable({
    id: `bundle:${assignmentId}`,
    data: { kind: "bundle", assignmentId },
  });

  useEffect(() => {
    if (!open) return undefined;
    // Menu renders in a portal, so treat clicks inside it as inside too.
    const onClick = (e) => {
      if (ref.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Move focus into the menu when it opens so keyboard users land on an item.
  useEffect(() => {
    if (open) itemRefs.current[0]?.focus();
  }, [open]);

  const hasCodes = unassignedCodes.length > 0;

  const closeMenu = ({ returnFocus = true } = {}) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  const toggleOpen = () => {
    if (!hasCodes) return;
    if (!open && ref.current) {
      const r = ref.current.getBoundingClientRect();
      setMenuPos({ left: r.left, top: r.bottom + 4 });
    }
    setOpen((v) => !v);
  };

  const handleMenuKeyDown = (e, i) => {
    if (e.key === "Escape") { e.preventDefault(); closeMenu(); }
    else if (e.key === "ArrowDown") { e.preventDefault(); itemRefs.current[Math.min(i + 1, unassignedCodes.length - 1)]?.focus(); }
    else if (e.key === "ArrowUp") { e.preventDefault(); itemRefs.current[Math.max(i - 1, 0)]?.focus(); }
    else if (e.key === "Home") { e.preventDefault(); itemRefs.current[0]?.focus(); }
    else if (e.key === "End") { e.preventDefault(); itemRefs.current[unassignedCodes.length - 1]?.focus(); }
    else if (e.key === "Tab") { closeMenu({ returnFocus: false }); }
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        alignItems: "center",
        backgroundColor: colors.blue50,
        border: `3px dashed ${isOver ? colors.blue700 : colors.blue500}`,
        borderRadius: 12,
        display: "flex",
        justifyContent: "center",
        minHeight: 120,
        padding: 16,
        position: "relative",
      }}
    >
      <div ref={ref} style={{ position: "relative" }}>
        <button
          ref={triggerRef}
          type="button"
          onClick={toggleOpen}
          disabled={!hasCodes}
          aria-haspopup="menu"
          aria-expanded={open}
          title={hasCodes ? "" : "No unassigned group codes available"}
          style={{
            alignItems: "center",
            background: "transparent",
            border: "none",
            color: colors.blue700,
            cursor: hasCodes ? "pointer" : "not-allowed",
            display: "inline-flex",
            fontSize: 18,
            fontWeight: 400,
            gap: 4,
            letterSpacing: "0.25px",
            lineHeight: "26px",
            opacity: hasCodes ? 1 : 0.5,
            padding: "0 8px",
          }}
        >
          Add Group Code
          <ChevronDownIcon />
        </button>
        {open && hasCodes && menuPos && createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label="Unassigned group codes"
            style={{
              backgroundColor: "white",
              border: `1px solid ${colors.blue300}`,
              borderRadius: 6,
              boxShadow: "0px 4px 12px rgba(14, 26, 49, 0.12)",
              left: menuPos.left,
              maxHeight: 220,
              minWidth: 180,
              overflowY: "auto",
              position: "fixed",
              top: menuPos.top,
              zIndex: 1000,
            }}
          >
            {unassignedCodes.map((code, i) => (
              <button
                key={code}
                ref={(el) => (itemRefs.current[i] = el)}
                type="button"
                role="menuitem"
                onClick={() => { onAddCode(code); closeMenu(); }}
                onKeyDown={(e) => handleMenuKeyDown(e, i)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: colors.nightlyWoods500,
                  cursor: "pointer",
                  display: "block",
                  fontSize: 14,
                  lineHeight: "20px",
                  padding: "8px",
                  textAlign: "left",
                  textTransform: "uppercase",
                  width: "100%",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.blue50; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
              >
                {code}
              </button>
            ))}
          </div>,
          document.body,
        )}
      </div>
    </div>
  );
};
